import { useRef, useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useCanvasSize } from '../hooks/useCanvasSize';
import { setupInteractions } from './interactionHandler';
import { createCollisionRenderer } from './collisionRenderer';
import { buildCollisionLayout } from './collisionGraphBuilder';
import { findArtistByName } from '../api/deezerClient';
import { clamp } from '../utils/mathUtils';

/**
 * Canvas component for the "Collide Universes" view.
 * Camera zoom controls LOD transitions automatically (LOD 4 → 1).
 */

// Detail panel sidebar width (matches panel.css). On viewports wider than
// PANEL_BREAKPOINT the panel renders as a right sidebar; below it becomes a
// top sheet that doesn't steal horizontal space.
const DETAIL_PANEL_WIDTH = 320;
const PANEL_BREAKPOINT = 480;

function computeFitTransform(items, viewWidth, viewHeight, padding = 60) {
  if (!items || items.length === 0) return { x: 0, y: 0, scale: 1 };

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const item of items) {
    if (item.x == null || item.y == null) continue;
    const r = item.radius || item.size || 5;
    minX = Math.min(minX, item.x - r);
    minY = Math.min(minY, item.y - r);
    maxX = Math.max(maxX, item.x + r);
    maxY = Math.max(maxY, item.y + r);
  }

  if (!isFinite(minX)) return { x: 0, y: 0, scale: 1 };

  const graphWidth = maxX - minX;
  const graphHeight = maxY - minY;
  if (graphWidth === 0 || graphHeight === 0) return { x: 0, y: 0, scale: 1 };

  const scaleX = (viewWidth - padding * 2) / graphWidth;
  const scaleY = (viewHeight - padding * 2) / graphHeight;
  const scale = clamp(Math.min(scaleX, scaleY), 0.05, 3);

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const x = viewWidth / 2 - cx * scale;
  const y = viewHeight / 2 - cy * scale;

  return { x, y, scale };
}

function animateTransform(stateRef, target, duration = 500) {
  const start = { ...stateRef.current.transform };
  const startTime = performance.now();

  function step(now) {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / duration, 1);
    const ease = 1 - Math.pow(1 - t, 3);

    stateRef.current.transform = {
      x: start.x + (target.x - start.x) * ease,
      y: start.y + (target.y - start.y) * ease,
      scale: start.scale + (target.scale - start.scale) * ease,
    };

    if (t < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

const CollisionCanvas = forwardRef(function CollisionCanvas(
  { collisionData, favorites, discoveredArtists, dislikes, onSelectNode, onHoverNode },
  ref
) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  const layoutRef = useRef(null);
  const hadDataRef = useRef(false);

  const stateRef = useRef({
    zoneMetas: [],
    allNodes: [],
    allLinks: [],
    worldBounds: { minX: 0, minY: 0, maxX: 0, maxY: 0 },
    transform: { x: 0, y: 0, scale: 1 },
    hoveredNode: null,
    selectedNode: null,
    favoriteNames: new Set(),
    dislikeNames: new Set(),
    discoveredNames: new Set(),
  });

  const rendererRef = useRef(null);
  const cleanupRef = useRef(null);
  const introEndsAtRef = useRef(0); // performance.now() timestamp when intro sequence finishes

  const size = useCanvasSize(containerRef);

  // Gate collision data updates: if the intro animation is still playing,
  // defer the update until it finishes so we don't cut the animation short.
  const [activeData, setActiveData] = useState(null);
  useEffect(() => {
    if (!collisionData) return;
    const now = performance.now();
    const remaining = introEndsAtRef.current - now;
    if (remaining > 0) {
      const timer = setTimeout(() => setActiveData(collisionData), remaining);
      return () => clearTimeout(timer);
    }
    setActiveData(collisionData);
  }, [collisionData]);

  // Build layout when active collision data changes
  useEffect(() => {
    if (!activeData) return;

    const layout = buildCollisionLayout(activeData);
    layoutRef.current = layout;

    stateRef.current.zoneMetas = layout.zoneMetas;
    stateRef.current.allNodes = layout.allNodes;
    stateRef.current.allLinks = layout.allLinks;
    stateRef.current.worldBounds = layout.worldBounds;
    stateRef.current.hoveredNode = null;
    stateRef.current.selectedNode = null;

    // Eagerly prefetch images for nodes that don't have one (e.g. shared
    // frontier artists) so artwork loads instantly in the detail panel.
    for (const node of layout.allNodes) {
      if (!node.image && !node.imageLarge) {
        findArtistByName(node.name)
          .then((artist) => {
            if (artist) {
              node.image = artist.imageLarge || artist.image || null;
            }
          })
          .catch(() => {});
      }
    }
  }, [activeData]);

  // Sync favorite/discovered/dislike names into renderer state for real-time ring effects
  useEffect(() => {
    stateRef.current.favoriteNames = new Set(
      (favorites || []).map((f) => f.artistName)
    );
  }, [favorites]);

  useEffect(() => {
    stateRef.current.discoveredNames = new Set(
      (discoveredArtists || []).map((d) => d.artistName)
    );
  }, [discoveredArtists]);

  useEffect(() => {
    stateRef.current.dislikeNames = new Set(
      (dislikes || []).map((d) => d.artistName)
    );
  }, [dislikes]);

  const fitAll = useCallback((animate = true) => {
    const layout = layoutRef.current;
    if (!layout || !layout.zoneMetas.length || size.width === 0) return;
    if (rendererRef.current) rendererRef.current.notifyInteraction();

    const items = layout.zoneMetas
      .filter((zm) => zm.count > 0)
      .map((zm) => ({
        x: zm.cx, y: zm.cy, radius: zm.visualRadius,
      }));
    const target = computeFitTransform(items, size.width, size.height, 40);

    // Wider view on initial load to show intro animation spread; tighter on reset
    const zoomBoost = animate ? 1.2 : 0.9;
    const cx = size.width / 2;
    const cy = size.height / 2;
    target.scale = clamp(target.scale * zoomBoost, 0.05, 3);

    // Center on the core overlap zone (world origin) so the collision
    // point sits in the middle of the screen, not the bounding-box center.
    // Fall back to shared frontier or friend's artists when there's no overlap.
    const coreZone = layout.zoneMetas.find((zm) => zm.key === 'core_overlap' && zm.count > 0)
      || layout.zoneMetas.find((zm) => zm.key === 'shared_frontier' && zm.count > 0)
      || layout.zoneMetas.find((zm) => zm.key === 'friend_artists' && zm.count > 0);
    const focusX = coreZone ? coreZone.cx : 0;
    const focusY = coreZone ? coreZone.cy : 0;
    target.x = cx - focusX * target.scale;
    target.y = cy - focusY * target.scale;

    if (animate) {
      animateTransform(stateRef, target, 500);
    } else {
      stateRef.current.transform = target;
    }
  }, [size.width, size.height]);

  const zoomToZone = useCallback((zoneKey) => {
    const layout = layoutRef.current;
    if (!layout) return;
    if (rendererRef.current) rendererRef.current.notifyInteraction();
    const zm = layout.zoneMetas.find((z) => z.key === zoneKey);
    if (!zm || size.width === 0 || zm.count === 0) return;

    const targetScale = Math.min(
      (size.width * 0.6) / (zm.visualRadius * 2),
      (size.height * 0.6) / (zm.visualRadius * 2),
      2.5
    );

    const target = {
      x: size.width / 2 - zm.cx * targetScale,
      y: size.height / 2 - zm.cy * targetScale,
      scale: targetScale,
    };

    animateTransform(stateRef, target, 700);
  }, [size.width, size.height]);

  const zoomBy = useCallback((factor) => {
    if (rendererRef.current) rendererRef.current.notifyInteraction();
    const t = stateRef.current.transform;
    const cx = size.width / 2;
    const cy = size.height / 2;
    const newScale = clamp(t.scale * factor, 0.05, 8);
    const ratio = newScale / t.scale;
    animateTransform(stateRef, {
      x: cx - (cx - t.x) * ratio,
      y: cy - (cy - t.y) * ratio,
      scale: newScale,
    }, 200);
  }, [size.width, size.height]);

  const zoomToNode = useCallback((node) => {
    if (!node || size.width === 0) return;
    if (rendererRef.current) rendererRef.current.notifyInteraction();
    const targetScale = clamp(1.5, 0.05, 8);
    // Offset center so the node sits in the visible area left of the sidebar
    const panelOffset = size.width > PANEL_BREAKPOINT ? DETAIL_PANEL_WIDTH / 2 : 0;
    const target = {
      x: size.width / 2 - panelOffset - node.x * targetScale,
      y: size.height / 2 - node.y * targetScale,
      scale: targetScale,
    };
    stateRef.current.selectedNode = node;
    if (onSelectNode) onSelectNode(node);
    animateTransform(stateRef, target, 700);
  }, [size.width, size.height, onSelectNode]);

  const followNode = useCallback((node) => {
    if (!node || node.x == null || node.y == null || size.width === 0 || size.height === 0) return;
    if (rendererRef.current) rendererRef.current.notifyInteraction();

    // Match by ID to get the renderer's own node reference so the
    // identity comparison (node === selectedNode) works for highlighting,
    // and so we use the renderer's current coordinates for centering.
    const nodes = stateRef.current.allNodes || [];
    const match = nodes.find((n) => n.id === node.id);
    const target_node = match || node;
    stateRef.current.selectedNode = target_node;

    // Gather the target node + direct neighbors for context framing
    const links = stateRef.current.allLinks || [];
    const neighbors = [target_node];
    for (const link of links) {
      const src = link.source;
      const tgt = link.target;
      if (src === target_node || src?.id === target_node.id) {
        if (tgt && tgt.x != null) neighbors.push(tgt);
      } else if (tgt === target_node || tgt?.id === target_node.id) {
        if (src && src.x != null) neighbors.push(src);
      }
    }

    // Compute scale that fits the node + neighbors, then bias tighter
    // so the target node feels prominent, not lost among neighbors
    const fit = computeFitTransform(neighbors, size.width, size.height, 80);
    const scale = clamp(fit.scale * 1.15, 1.1, 1.8);

    // Offset center so the node sits in the visible area left of the sidebar
    const panelOffset = size.width > PANEL_BREAKPOINT ? DETAIL_PANEL_WIDTH / 2 : 0;

    // Center on the target node (not the neighbor centroid)
    const target = {
      x: size.width / 2 - panelOffset - target_node.x * scale,
      y: size.height / 2 - target_node.y * scale,
      scale,
    };

    // Use a gentle ease-in-out curve for a smooth glide between nodes
    const start = { ...stateRef.current.transform };
    const startTime = performance.now();
    const duration = 1400;

    function step(now) {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Ease-in-out sine: smooth acceleration and deceleration
      const ease = -(Math.cos(Math.PI * t) - 1) / 2;

      stateRef.current.transform = {
        x: start.x + (target.x - start.x) * ease,
        y: start.y + (target.y - start.y) * ease,
        scale: start.scale + (target.scale - start.scale) * ease,
      };

      if (t < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }, [size.width, size.height]);

  const getNodes = useCallback(() => {
    return stateRef.current.allNodes || [];
  }, []);

  const captureImage = useCallback((options = {}) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    if (options.watermark) {
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      ctx.save();
      ctx.font = `600 ${14 * dpr}px 'Space Grotesk', sans-serif`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.textAlign = 'right';
      ctx.fillText('Stellar', canvas.width - 16 * dpr, canvas.height - 12 * dpr);
      ctx.restore();
    }

    return canvas.toDataURL('image/png');
  }, []);

  useImperativeHandle(ref, () => ({
    resetView: () => fitAll(true),
    zoomToZone,
    zoomBy,
    zoomToNode,
    followNode,
    getNodes,
    captureImage,
  }), [fitAll, zoomToZone, zoomBy, zoomToNode, followNode, getNodes, captureImage]);

  // Set up canvas, renderer, and interactions
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !layoutRef.current || size.width === 0 || size.height === 0) return;
    if (!layoutRef.current.zoneMetas.length) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.width * dpr;
    canvas.height = size.height * dpr;
    canvas.style.width = size.width + 'px';
    canvas.style.height = size.height + 'px';

    // Snap to overview on initial load; re-center smoothly on data refreshes
    // so the camera doesn't point at empty space after a layout rebuild.
    const isUpdate = hadDataRef.current;
    hadDataRef.current = true;
    if (!isUpdate) {
      fitAll(false);
    } else {
      fitAll(true);
    }

    // After the collision intro animation finishes (2s), smoothly zoom in
    // to the shared core at LOD 2 so artist labels become visible.
    // Skip on data updates (recollision) to avoid stuttery replay.
    let introZoomTimer = null;
    if (!isUpdate) {
      const INTRO_DURATION = 2000;
      const ZOOM_DELAY = INTRO_DURATION + 300; // brief pause after intro settles
      const ZOOM_DURATION = 2000;
      // Mark when the full intro sequence (slide-in + zoom glide) will end,
      // so data updates that arrive mid-intro get deferred.
      introEndsAtRef.current = performance.now() + ZOOM_DELAY + ZOOM_DURATION;
      introZoomTimer = setTimeout(() => {
        const layout = layoutRef.current;
        if (!layout) return;
        // Prefer core overlap; fall back to shared frontier, then friend's artists
        const coreZone = layout.zoneMetas.find((zm) => zm.key === 'core_overlap' && zm.count > 0)
          || layout.zoneMetas.find((zm) => zm.key === 'shared_frontier' && zm.count > 0)
          || layout.zoneMetas.find((zm) => zm.key === 'friend_artists' && zm.count > 0);
        if (!coreZone) return;

        // Target scale in LOD 2 range where selective labels show
        const targetScale = 0.55;
        const cx = size.width / 2;
        const cy = size.height / 2;
        const focusX = coreZone.cx;
        const focusY = coreZone.cy;
        const target = {
          x: cx - focusX * targetScale,
          y: cy - focusY * targetScale,
          scale: targetScale,
        };

        // Cinematic ease-in-out for the zoom glide
        const start = { ...stateRef.current.transform };
        const startTime = performance.now();

        function step(now) {
          const elapsed = now - startTime;
          const t = Math.min(elapsed / ZOOM_DURATION, 1);
          // Ease-in-out sine for smooth acceleration/deceleration
          const ease = -(Math.cos(Math.PI * t) - 1) / 2;

          stateRef.current.transform = {
            x: start.x + (target.x - start.x) * ease,
            y: start.y + (target.y - start.y) * ease,
            scale: start.scale + (target.scale - start.scale) * ease,
          };

          if (t < 1) {
            requestAnimationFrame(step);
          }
        }

        requestAnimationFrame(step);
      }, ZOOM_DELAY);
    }

    if (rendererRef.current) rendererRef.current.stop();
    const renderer = createCollisionRenderer(canvas, () => stateRef.current, {
      skipIntro: isUpdate,
      // Lets the renderer know when the full intro (slide-in + zoom glide)
      // ends so it can defer expensive per-node effects until then.
      getIntroEndTime: () => introEndsAtRef.current,
    });
    rendererRef.current = renderer;
    renderer.start();

    if (cleanupRef.current) cleanupRef.current();

    const cleanup = setupInteractions(
      canvas,
      () => {
        const scale = stateRef.current.transform.scale;
        if (scale < 0.35) {
          // At overview zoom, return zone centers as clickable targets
          return (stateRef.current.zoneMetas || [])
            .filter((zm) => zm.count > 0)
            .map((zm) => ({
              x: zm.cx,
              y: zm.cy,
              radius: zm.visualRadius,
              _isZoneCenter: true,
              _zoneKey: zm.key,
              name: zm.label,
            }));
        }
        return stateRef.current.allNodes || [];
      },
      () => stateRef.current.transform,
      {
        onHover: (node) => {
          if (node && node._isZoneCenter) {
            stateRef.current.hoveredNode = null;
          } else {
            stateRef.current.hoveredNode = node;
          }
          if (onHoverNode) onHoverNode(node);
        },
        onClick: (node) => {
          renderer.notifyInteraction();
          if (node && node._isZoneCenter) {
            zoomToZone(node._zoneKey);
            return;
          }
          stateRef.current.selectedNode = node || null;
          if (onSelectNode) onSelectNode(node || null);
        },
        onPan: (dx, dy) => {
          renderer.notifyInteraction();
          const t = stateRef.current.transform;
          stateRef.current.transform = { ...t, x: t.x + dx, y: t.y + dy };
        },
        onZoom: (delta, cx, cy) => {
          renderer.notifyInteraction();
          const t = stateRef.current.transform;
          const newScale = clamp(t.scale * delta, 0.05, 8);
          const ratio = newScale / t.scale;
          stateRef.current.transform = {
            x: cx - (cx - t.x) * ratio,
            y: cy - (cy - t.y) * ratio,
            scale: newScale,
          };
        },
      }
    );
    cleanupRef.current = cleanup;

    return () => {
      if (introZoomTimer) clearTimeout(introZoomTimer);
      renderer.stop();
      rendererRef.current = null;
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [activeData, size.width, size.height, fitAll, zoomToZone, onSelectNode, onHoverNode]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        style={{ display: 'block', position: 'absolute', top: 0, left: 0, cursor: 'grab' }}
      />
    </div>
  );
});

export default CollisionCanvas;
